import React from "react";
import { BRAND_NAME, ENDORSEMENT, TAGLINE_PARTS } from "@/lib/brand";

/**
* BrandMark: renders the official"Destino Europe" bird using the exported
 * PNG from the brand book. To support multiple brand colors without shipping
 * multiple PNGs (and without the destructive PIL recolor pipeline we had
 * before), we use the PNG as a CSS `mask-image`. The alpha channel of the PNG
 * defines the silhouette and the background color paints it in the desired
 * brand color. This keeps pixel-perfect fidelity with the brand book while
 * letting the logo adapt to light or dark surfaces.
 */
const BIRD_SRC = "/brand/destino-bird.png";

// Aspect ratio of the source PNG (256 × 321 → ~0.797)
const BIRD_ASPECT = 321 / 256;

const COLORS = {
  burgundy: "var(--mte-burgundy, #451C25)",
  gold: "var(--mte-gold-2, #B3A17D)",
  ivory: "#F5EFE7",
  wine: "#2E0F14",
  white: "#FFFFFF",
};

export function BrandMark({ size = 28, variant = "burgundy", className = "" }) {
  const color = COLORS[variant] || COLORS.burgundy;
  const height = Math.round(size * BIRD_ASPECT);
  return (
    <span
      aria-hidden="true"
      className={className}
      style={{
        display: "inline-block",
        width: size,
        height,
        backgroundColor: color,
        WebkitMaskImage: `url(${BIRD_SRC})`,
        maskImage: `url(${BIRD_SRC})`,
        WebkitMaskRepeat: "no-repeat",
        maskRepeat: "no-repeat",
        WebkitMaskSize: "contain",
        maskSize: "contain",
        WebkitMaskPosition: "center",
        maskPosition: "center",
      }}
    />
  );
}

/**
* BrandLogo: mark + wordmark. Lockup variants are MUTUALLY EXCLUSIVE (brand rule):
 *   variant="plain"       → DESTINO EUROPE
 *   variant="endorsement" → DESTINO EUROPE / by Espíritu Travel
 *   variant="tagline"     → DESTINO EUROPE / Your new life, together.
 * Tagline + endorsement can never render together. `layout` = "horizontal" | "stacked".
 */
const LOCKUPS = ["plain", "endorsement", "tagline"];

export function BrandLogo({
  variant = "plain",
  layout = "horizontal",
  color = "var(--mte-burgundy)",
  markVariant = "burgundy",
  subColor = "var(--mte-gold-2)",
  size = "md",
}) {
  const lockup = LOCKUPS.includes(variant) ? variant : "plain";
  const sizes = {
    sm: { mark: 30, text: "text-lg", tag: "text-[12px]", end: "text-[11px]", gap: "gap-2.5" },
    md: { mark: 40, text: "text-2xl", tag: "text-[13px]", end: "text-[12px]", gap: "gap-3" },
    lg: { mark: 60, text: "text-3xl md:text-4xl", tag: "text-sm", end: "text-[13px]", gap: "gap-4" },
    xl: { mark: 84, text: "text-4xl md:text-5xl", tag: "text-base", end: "text-sm", gap: "gap-5" },
  };
  const s = sizes[size] || sizes.md;
  const stacked = layout === "stacked";

  // Exactly one (or zero) sub-line is rendered, decided by a single switch.
  let subline = null;
  if (lockup === "endorsement") {
    subline = (
      <div
        data-testid="brand-endorsement"
        className={`${stacked ? "mt-1.5" : "mt-0.5"} ${s.end} tracking-[0.08em] font-sans`}
        style={{ color: subColor, fontWeight: 400 }}
      >
        {ENDORSEMENT}
      </div>
    );
  } else if (lockup === "tagline") {
    subline = (
      <div
        data-testid="brand-tagline"
        className={`${stacked ? "mt-2" : "mt-1"} ${s.tag} font-serif tracking-normal`}
        style={{ color: subColor, fontWeight: 400, fontStyle: "italic" }}
      >
        {TAGLINE_PARTS.before}{TAGLINE_PARTS.emphasis}{TAGLINE_PARTS.after}
      </div>
    );
  }

  if (stacked) {
    return (
      <div className="flex flex-col items-center text-center" data-lockup={lockup}>
        <BrandMark size={Math.round(s.mark * 1.6)} variant={markVariant} className="mb-4" />
        <div
          className={`font-display uppercase tracking-[0.32em] ${s.text}`}
          style={{ color, fontWeight: 500 }}
        >
          {BRAND_NAME}
        </div>
        {subline}
      </div>
    );
  }

  return (
    <div className={`inline-flex items-center ${s.gap}`} data-lockup={lockup}>
      <BrandMark size={s.mark} variant={markVariant} />
      <div className="leading-tight">
        <div
          className={`font-display uppercase tracking-[0.28em] ${s.text}`}
          style={{ color, fontWeight: 500 }}
        >
          {BRAND_NAME}
        </div>
        {subline}
      </div>
    </div>
  );
}

export default BrandLogo;
